import { Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import type { RecurrenceFrequency, RecurrenceMonthlyMode } from "@/api/appointments";
import { WEEKDAY_NAMES, WEEKDAY_TOKENS, type RuleCore } from "../lib/recurrence";

/**
 * Calendar — the "Custom…" expansion of the Repeats row.
 *
 * Rendered INLINE beneath the Repeats select rather than in a modal of its own:
 * the appointment form already scrolls, and a second overlay would put the rule
 * being built on a different surface from the Start date it is anchored to —
 * which is the one field a reader has to see to know what "the third Tuesday"
 * means.
 *
 * 🔴 PURE AND CONTROLLED. It holds no state, fetches nothing, and derives every
 * label from the {@link RuleCore} it is handed plus the form's own Start date.
 * The anchor is never converted to an instant here — see the module note in
 * ../lib/recurrence.ts.
 */

interface Props {
  value: RuleCore;
  onChange: (next: RuleCore) => void;
  /** The form's Start date (`YYYY-MM-DD`) — the anchor the labels describe. */
  startDate: string;
  disabled?: boolean;
}

/**
 * Interval bounds, mirroring `RecurrenceLimits.MinInterval` / `MaxInterval`.
 * The server validates them independently; this only stops a mistyped number
 * from becoming a rule that generates one occurrence and looks broken.
 */
const MIN_INTERVAL = 1;
const MAX_INTERVAL = 366;

const FREQUENCIES: { value: RecurrenceFrequency; unit: string; units: string }[] = [
  { value: "Daily", unit: "day", units: "days" },
  { value: "Weekly", unit: "week", units: "weeks" },
  { value: "Monthly", unit: "month", units: "months" },
  { value: "Yearly", unit: "year", units: "years" },
];

/**
 * 🔴 ORDINALS RUN 1–5 AND 5 MEANS "the fifth", NOT "the last".
 * `RecurrenceExpander` computes `1 + offset + (ordinal - 1) * 7` and returns
 * nothing when that overruns the month, so a "fifth Tuesday" series simply
 * SKIPS the months without one. Labelling 5 as "last" would promise a monthly
 * cadence the engine does not deliver. "The last day of the month" is the
 * separate `LastDayOfMonth` mode below, which is about the date, not a weekday.
 */
const ORDINALS: { value: number; label: string }[] = [
  { value: 1, label: "first" },
  { value: 2, label: "second" },
  { value: 3, label: "third" },
  { value: 4, label: "fourth" },
  { value: 5, label: "fifth" },
];

/** The anchor's weekday index, or null when the Start date isn't usable yet. */
function anchorWeekday(startDate: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return null;
  const [y, m, d] = startDate.split("-").map(Number);
  const at = new Date(Date.UTC(y, m - 1, d));
  return Number.isNaN(at.getTime()) ? null : at.getUTCDay();
}

/** The anchor's day-of-month, or null. */
function anchorDayOfMonth(startDate: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return null;
  return Number(startDate.slice(8, 10)) || null;
}

export function RecurrenceBuilder({ value, onChange, startDate, disabled = false }: Props) {
  const selectedDays = (value.byWeekday ?? "").split(",").filter(Boolean);

  /**
   * Switching frequency seeds the fields that frequency requires, so the rule is
   * never momentarily invalid. A weekly rule with no weekday and a monthly rule
   * with no mode are both refused by `RecurrenceRule.Validate` server-side.
   */
  const setFrequency = (next: RecurrenceFrequency) => {
    if (next === "Weekly") {
      const day = anchorWeekday(startDate);
      onChange({
        frequency: "Weekly",
        interval: value.interval,
        byWeekday: selectedDays.length
          ? selectedDays.join(",")
          : WEEKDAY_TOKENS[day ?? 1],
      });
      return;
    }
    if (next === "Monthly") {
      onChange({
        frequency: "Monthly",
        interval: value.interval,
        monthlyMode: value.monthlyMode ?? "ByDayOfMonth",
        monthlyDayOfMonth: value.monthlyDayOfMonth ?? anchorDayOfMonth(startDate) ?? 1,
        monthlyWeekdayOrdinal: value.monthlyWeekdayOrdinal ?? 1,
        monthlyWeekday: value.monthlyWeekday ?? WEEKDAY_TOKENS[anchorWeekday(startDate) ?? 1],
      });
      return;
    }
    onChange({ frequency: next, interval: value.interval });
  };

  const toggleDay = (token: string) => {
    const next = selectedDays.includes(token)
      ? selectedDays.filter((d) => d !== token)
      : [...selectedDays, token];
    // Emitted in weekday order (Sun→Sat) to match the server's own canonical
    // `RecurrenceRule.ByWeekdayToken()`, so a saved rule round-trips through
    // `presetForRule` on the identical string it was stored as.
    const ordered = WEEKDAY_TOKENS.filter((t) => next.includes(t)).join(",");
    onChange({ ...value, byWeekday: ordered });
  };

  const setMonthlyMode = (mode: RecurrenceMonthlyMode) =>
    onChange({
      ...value,
      monthlyMode: mode,
      monthlyDayOfMonth: value.monthlyDayOfMonth ?? anchorDayOfMonth(startDate) ?? 1,
      monthlyWeekdayOrdinal: value.monthlyWeekdayOrdinal ?? 1,
      monthlyWeekday: value.monthlyWeekday ?? WEEKDAY_TOKENS[anchorWeekday(startDate) ?? 1],
    });

  // A weekly rule with nothing ticked cannot be expanded; say so where the
  // boxes are rather than letting Save fail with a server message.
  const noWeekdayChosen = value.frequency === "Weekly" && selectedDays.length === 0;

  return (
    <fieldset
      disabled={disabled}
      className="m-0 mt-space-2 flex flex-col gap-space-3 rounded-md border border-border-light bg-white-warm px-space-3 py-space-3"
    >
      <legend className="sr-only">Custom repeat</legend>

      <div className="flex flex-wrap items-center gap-space-2">
        <span className="text-small text-onyx">Repeat every</span>
        <Input
          type="number"
          min={MIN_INTERVAL}
          max={MAX_INTERVAL}
          aria-label="Repeat interval"
          value={String(value.interval)}
          className="w-[5rem]"
          onChange={(e) => {
            // An empty or unparsable box holds at the minimum rather than
            // writing NaN into the rule; the field itself shows what the user
            // typed until it parses.
            const n = Number(e.target.value);
            onChange({
              ...value,
              interval: Number.isFinite(n)
                ? Math.min(MAX_INTERVAL, Math.max(MIN_INTERVAL, Math.trunc(n)))
                : MIN_INTERVAL,
            });
          }}
        />
        <Select
          aria-label="Repeat frequency"
          value={value.frequency}
          className="w-auto"
          onChange={(e) => setFrequency(e.target.value as RecurrenceFrequency)}
        >
          {FREQUENCIES.map((f) => (
            <option key={f.value} value={f.value}>
              {value.interval === 1 ? f.unit : f.units}
            </option>
          ))}
        </Select>
      </div>

      {value.frequency === "Weekly" && (
        <div className="flex flex-col gap-space-1">
          <span className="text-label text-gray-mid">On these days</span>
          <div className="flex flex-wrap gap-space-2" role="group" aria-label="Repeat on">
            {WEEKDAY_TOKENS.map((token, i) => (
              <label
                key={token}
                className="flex items-center gap-space-1 text-small text-onyx"
              >
                <input
                  type="checkbox"
                  className="accent-gold"
                  checked={selectedDays.includes(token)}
                  aria-label={WEEKDAY_NAMES[i]}
                  onChange={() => toggleDay(token)}
                />
                {WEEKDAY_NAMES[i].slice(0, 3)}
              </label>
            ))}
          </div>
          {noWeekdayChosen && (
            <p className="m-0 text-label text-status-error" role="alert">
              Pick at least one day.
            </p>
          )}
        </div>
      )}

      {value.frequency === "Monthly" && (
        <div
          className="flex flex-col gap-space-2"
          role="radiogroup"
          aria-label="Monthly repeat pattern"
        >
          <div className="flex flex-wrap items-center gap-space-2">
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="monthly-mode"
                className="accent-gold"
                checked={(value.monthlyMode ?? "ByDayOfMonth") === "ByDayOfMonth"}
                onChange={() => setMonthlyMode("ByDayOfMonth")}
              />
              On day
            </label>
            <Input
              type="number"
              min={1}
              max={31}
              aria-label="Day of month"
              className="w-[4.5rem]"
              value={String(value.monthlyDayOfMonth ?? anchorDayOfMonth(startDate) ?? 1)}
              disabled={(value.monthlyMode ?? "ByDayOfMonth") !== "ByDayOfMonth"}
              onChange={(e) => {
                const n = Number(e.target.value);
                onChange({
                  ...value,
                  monthlyMode: "ByDayOfMonth",
                  monthlyDayOfMonth: Number.isFinite(n)
                    ? Math.min(31, Math.max(1, Math.trunc(n)))
                    : 1,
                });
              }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-space-2">
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="radio"
                name="monthly-mode"
                className="accent-gold"
                checked={value.monthlyMode === "ByWeekdayPosition"}
                onChange={() => setMonthlyMode("ByWeekdayPosition")}
              />
              On the
            </label>
            <Select
              aria-label="Weekday position"
              className="w-auto"
              value={String(value.monthlyWeekdayOrdinal ?? 1)}
              disabled={value.monthlyMode !== "ByWeekdayPosition"}
              onChange={(e) =>
                onChange({
                  ...value,
                  monthlyMode: "ByWeekdayPosition",
                  monthlyWeekdayOrdinal: Number(e.target.value),
                })
              }
            >
              {ORDINALS.map((o) => (
                <option key={o.value} value={String(o.value)}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Weekday of month"
              className="w-auto"
              value={value.monthlyWeekday ?? WEEKDAY_TOKENS[anchorWeekday(startDate) ?? 1]}
              disabled={value.monthlyMode !== "ByWeekdayPosition"}
              onChange={(e) =>
                onChange({
                  ...value,
                  monthlyMode: "ByWeekdayPosition",
                  monthlyWeekday: e.target.value,
                })
              }
            >
              {WEEKDAY_TOKENS.map((token, i) => (
                <option key={token} value={token}>
                  {WEEKDAY_NAMES[i]}
                </option>
              ))}
            </Select>
          </div>

          <label className="flex items-center gap-space-2 text-small text-onyx">
            <input
              type="radio"
              name="monthly-mode"
              className="accent-gold"
              checked={value.monthlyMode === "LastDayOfMonth"}
              onChange={() =>
                onChange({
                  ...value,
                  monthlyMode: "LastDayOfMonth",
                  monthlyDayOfMonth: null,
                  monthlyWeekdayOrdinal: null,
                  monthlyWeekday: null,
                })
              }
            />
            On the last day of the month
          </label>

          {value.monthlyWeekdayOrdinal === 5 &&
            value.monthlyMode === "ByWeekdayPosition" && (
              <p className="m-0 text-caption text-gray-mid" role="status">
                Months without a fifth of that weekday are skipped.
              </p>
            )}
        </div>
      )}
    </fieldset>
  );
}
